import { Box, Card, Container, Step, StepLabel, stepLabelClasses, Stepper, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Provider, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useMatch, useSearchParams } from 'react-router';
import {
  Availability_Status_Enum,
  Base_Unit_Type_Enum,
  Billing_Type_Enum,
  Duration_Type_Enum,
  Product_Label_Name_Enum,
  Product_Sub_Family_Name_Enum,
  Trade_Item_Unit_Descriptor_Enum,
} from '@kheops/graphql';
import { BaseUnit, FormatPackagingUnitPrice, FormatPackagingVolumePrice, LogisticalPackaging, round } from '@kheops/utils';
import { currentContextAtom } from '../../state';
import useBaseStore from '../../hooks/useBaseStore';
import { useProductByIdLazyQuery } from '../../queries/__generated__/productById.generated';
import { RoutePaths } from '../../routes/AppRoutes';
import { FeatureFlagKey, isFeatureEnabled } from '../../common/utils/featureFlags';
import { PageWrapper } from '../../common/components/PageWrapper';
import BackButton from '../../common/components/BackButton';
import TopAppBar from '../../common/components/TopAppBar';
import ProductAdd from './ProductAdd';
import { companyIdAtom, DraftPhoto, DraftProduct, draftProductAtom, DraftProductPackagings, draftProductPackagingsAtom, editModeAtom, namingWizardBackHandlerAtom, ProductPackagingEditMode } from './state';

export default function ProductAddPage(): React.JSX.Element {
  const { t } = useTranslation(['products', 'price']);
  const theme = useTheme();
  const matchProductAddInfo = useMatch(RoutePaths.PRODUCT_ADD_INFO);
  const { companyId } = useAtomValue(currentContextAtom);
  const [searchParams] = useSearchParams();
  const [getProductById, { data: productData }] = useProductByIdLazyQuery();
  const productId = searchParams.get('productId');
  const baseUnitSku = parseInt(searchParams.get('baseUnitSku') || '', 10);
  const isSmallerThanLg = useMediaQuery(theme.breakpoints.down('lg'));
  const [baseStoreReady, setBaseStoreReady] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const location = useLocation();
  const baseStore = useBaseStore();
  const namingWizardBackHandler = useAtomValue(namingWizardBackHandlerAtom, { store: baseStore })?.back;
  const steps = [
    { path: RoutePaths.PRODUCT_ADD_INFO, label: t('products:identification') },
    ...(isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION)
      ? [{ path: RoutePaths.PRODUCT_ADD_VISIBILITY, label: t('products:visibility') }]
      : []),
    { path: RoutePaths.PRODUCT_ADD_SPECIFICATIONS, label: t('products:specifications') },
    { path: RoutePaths.PRODUCT_ADD_PACKAGING, label: t('products:sales_unit') },
  ];
  const activeStepIndex = steps.findIndex(({ path }) => path === location.pathname);

  const backUrl = useMemo((): string => {
    const product = productData?.product_by_pk;

    return product
      ? generatePath(RoutePaths.PRODUCT, { productSku: `${product.sku}`, companyId: product.company_id })
      : RoutePaths.COMPANY_PRODUCTS;
  }, [productData]);

  const getBaseUnitPrice = (baseUnit: BaseUnit, packaging: LogisticalPackaging): number => {
    let initialBaseUnitPrice: number;

    if (baseUnit.billing_type === Billing_Type_Enum.WeightDependent || baseUnit.unit_type === Base_Unit_Type_Enum.Bulk) {
      const packagingVolumePrice = FormatPackagingVolumePrice(
        packaging,
        baseUnit,
      );

      initialBaseUnitPrice = packagingVolumePrice.value;
    } else {
      initialBaseUnitPrice = FormatPackagingUnitPrice(packaging);
    }

    return round(initialBaseUnitPrice, 3);
  };

  useEffect(() => {
    let draftProduct: DraftProduct | undefined;
    const draftProductPackagings: DraftProductPackagings[] = [];

    if (productData?.product_by_pk) {
      const product = productData.product_by_pk;

      draftProduct = {
        sku: product.sku,
        name: product.name,
        company_id: product.company_id,
        labels: product.labels.map((label) => label.label) as Product_Label_Name_Enum[],
        sub_family: product.sub_family as Product_Sub_Family_Name_Enum,
        vat_rate: product.vat_rate!,
        ingredients: product.ingredients,
        allergens: product.allergens,
        calories: product.calories,
        calories_kj: product.calories_kj,
        fat: product.fat,
        saturated_fat: product.saturated_fat,
        carbohydrate: product.carbohydrate,
        sugars: product.sugars,
        sodium: product.sodium,
        protein: product.protein,
        description: product.description,
        shelf_life_duration: product.shelf_life_duration,
        shelf_life_duration_type: product.shelf_life_duration_type || Duration_Type_Enum.Days,
        brand: product.brand,
        alcohol_by_volume: product.alcohol_by_volume,
        is_exempt_from_ingredient_list: product.is_exempt_from_ingredient_list,
        has_no_allergen_declaration: product.has_no_allergen_declaration,
        preservation_method: product.preservation_method,
        generic_product_code: product.generic_product_code,
        classification_tag_codes: product.classification_tags.map((tag) => tag.tag_code),
        classification_tags_none: false,
      };

      product.base_units.forEach((base_unit) => {
        const packagings = base_unit.packagings.map((packaging) => ({
          sku: packaging.sku,
          trade_item_unit_descriptor: packaging.trade_item_unit_descriptor as Trade_Item_Unit_Descriptor_Enum,
          unit_quantity: packaging.unit_quantity,
          net_content: packaging.net_content,
          content_measurement_unit: packaging.content_measurement_unit,
          price: packaging.price,
          base_unit_price: getBaseUnitPrice(base_unit, packaging),
        }));

        draftProductPackagings.push({
          csu: {
            sku: base_unit.sku!,
            csu_type: base_unit.unit_type === Base_Unit_Type_Enum.Bulk ? 'BULK' : 'UNIT',
            unit_quantity: base_unit.unit_quantity!,
            unit_type: base_unit.unit_type === Base_Unit_Type_Enum.Bulk ? '' as Base_Unit_Type_Enum : base_unit.unit_type!,
            unit_net_content: base_unit.unit_net_content!,
            content_measurement_unit: base_unit.content_measurement_unit,
            gtin: base_unit.gtin,
            billing_type: base_unit.billing_type,
            availability: base_unit.availability === Availability_Status_Enum.Available,
            main_photo: base_unit.main_photo,
            suggested_retail_price: base_unit.suggested_retail_price,
            photos: base_unit.photos.reduce((acc, { photo }) => {
              const draftPhoto = {
                domain: photo.domain,
                path: photo.path,
              };

              if (photo.path === base_unit.main_photo?.path) {
                acc.unshift(draftPhoto);
              } else {
                acc.push(draftPhoto);
              }

              return acc;
            }, [] as DraftPhoto[]),
          },
          packagings,
        });
      });

      baseStore.set(draftProductAtom, draftProduct);
      baseStore.set(draftProductPackagingsAtom, draftProductPackagings);
      setBaseStoreReady(true);
    }
  }, [productData]);

  useEffect(() => {
    let editMode: ProductPackagingEditMode;

    baseStore.set(companyIdAtom, companyId!);

    if (productId) {
      editMode = 'EDIT';

      if (matchProductAddInfo) {
        setFormTitle(t('products:modify_my_product'));
      } else if (baseUnitSku) {
        setFormTitle(t('products:modify_a_base_unit'));
      } else {
        editMode = 'ADD';
        setFormTitle(t('products:add_a_base_unit'));
      }

      getProductById({
        variables: {
          productId,
        },
      });
    } else {
      editMode = 'CREATE';
      setFormTitle(t('products:add_a_product'));
      setBaseStoreReady(true);
    }

    baseStore.set(editModeAtom, editMode);
  }, [matchProductAddInfo]);

  return (
    <>
      <TopAppBar
        configuration="small"
        backTo={backUrl}
        title={formTitle}
        startAction={<BackButton to={backUrl} size="large" compact onClick={namingWizardBackHandler} />}
      />
      <PageWrapper
        sx={{
          pt: {
            xs: 1,
            lg: 2,
          },
        }}
      >
        {baseStoreReady
          && (
            <Provider store={baseStore}>
              <Container
                maxWidth="md"
                fixed
                disableGutters
              >
                {!productId && (
                  <Box
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      px: 1,
                      mb: 2,
                    }}
                  >
                    <Stepper
                      activeStep={activeStepIndex}
                      sx={{
                        width: '100%',
                        [`& .${stepLabelClasses.labelContainer}`]: {
                          display: 'none',
                        },
                      }}
                    >
                      {steps.map(({ path, label }) => (
                        <Step key={path}>
                          <StepLabel>{label}</StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  </Box>
                )}
                <Card sx={{ p: { xs: 2, sm: 4 }, mb: 2 }}>
                  {!isSmallerThanLg && (
                    <Box sx={{ mb: 4, display: 'flex', gap: 1 }}>
                      <BackButton
                        variant="text"
                        to={backUrl}
                        compact
                        onClick={namingWizardBackHandler}
                      />

                      <Typography variant="titleLarge" sx={{ alignSelf: 'center' }}>
                        {formTitle}
                      </Typography>
                    </Box>
                  )}
                  {!productId && (
                    <>
                      <Typography
                        variant="titleLarge"
                        sx={{
                          display: { xs: 'block', md: 'none' },
                          mb: 3,
                        }}
                      >
                        {steps[activeStepIndex]?.label}
                      </Typography>
                      <Stepper
                        activeStep={activeStepIndex}
                        sx={{
                          display: { xs: 'none', md: 'flex' },
                          width: '100%',
                          mb: 4,
                          [`& .${stepLabelClasses.label}`]: {
                            whiteSpace: 'normal',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                          },
                        }}
                      >
                        {steps.map(({ path, label }) => (
                          <Step key={path}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </>
                  )}
                  <ProductAdd />
                </Card>
              </Container>
            </Provider>
          )}
      </PageWrapper>
    </>
  );
}
